import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LangLink, useLang } from '@/i18n/LangContext';
import {
  disableGoogleAnalytics,
  enableGoogleAnalytics,
  getAnalyticsConsent,
  trackGooglePageView,
} from '@/lib/googleAnalytics';

export function AnalyticsPageViewTracker() {
  const location = useLocation();
  const lastTrackedPath = useRef(null);
  const currentPath = `${location.pathname}${location.search}`;

  useEffect(() => {
    const trackCurrentPage = () => {
      if (lastTrackedPath.current === currentPath) return;
      if (getAnalyticsConsent() !== 'granted') return;
      trackGooglePageView(currentPath);
      lastTrackedPath.current = currentPath;
    };

    trackCurrentPage();
    window.addEventListener('unity:analytics-ready', trackCurrentPage);
    return () => window.removeEventListener('unity:analytics-ready', trackCurrentPage);
  }, [currentPath]);

  return null;
}

export function CookieConsent() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const storedConsent = getAnalyticsConsent();
    setAnalyticsEnabled(storedConsent === 'granted');

    if (storedConsent === 'granted') {
      enableGoogleAnalytics();
    } else if (storedConsent === 'denied') {
      disableGoogleAnalytics();
    } else {
      setVisible(true);
    }

    const openPreferences = () => {
      const currentConsent = getAnalyticsConsent();
      setAnalyticsEnabled(currentConsent === 'granted');
      setDetailsOpen(true);
      setVisible(true);
    };

    window.addEventListener('unity:open-cookie-settings', openPreferences);
    return () => window.removeEventListener('unity:open-cookie-settings', openPreferences);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && detailsOpen) setDetailsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [detailsOpen, visible]);

  const finish = (allowAnalytics) => {
    setAnalyticsEnabled(allowAnalytics);
    if (allowAnalytics) enableGoogleAnalytics();
    else disableGoogleAnalytics();
    setVisible(false);
    setDetailsOpen(false);
  };

  if (!visible) return null;

  return (
    <section
      role="region"
      aria-label={t('cookieTitle')}
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-white shadow-[0_-18px_55px_rgba(0,0,0,0.16)]"
      data-testid="cookie-consent"
    >
      <div className="mx-auto max-w-site px-5 md:px-8 py-5 md:py-6">
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <h2 className="font-heading text-lg font-bold text-foreground mb-1.5">{t('cookieTitle')}</h2>
              <p className="text-sm leading-relaxed text-foreground/70">
                {t('cookieText')}{' '}
                <LangLink to="/cookie-policy" className="font-semibold text-primary underline underline-offset-4">
                  {t('cookiePolicyLink')}
                </LangLink>
              </p>
            </div>

            {!detailsOpen && (
              <div className="flex flex-col sm:flex-row gap-2.5 lg:shrink-0">
                <button
                  type="button"
                  onClick={() => finish(false)}
                  className="rounded-md border border-foreground/20 px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  {t('cookieReject')}
                </button>
                <button
                  type="button"
                  onClick={() => setDetailsOpen(true)}
                  className="rounded-md border border-primary/30 px-5 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-brand-pale"
                >
                  {t('cookieCustomize')}
                </button>
                <button
                  type="button"
                  onClick={() => finish(true)}
                  className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  {t('cookieAccept')}
                </button>
              </div>
            )}
          </div>

          {detailsOpen && (
            <div className="border-t border-border pt-5" data-testid="cookie-preferences-panel">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h3 className="font-heading text-sm font-bold text-foreground">{t('cookieNecessaryTitle')}</h3>
                    <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-primary">
                      {t('cookieAlwaysOn')}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed text-foreground/65">{t('cookieNecessaryText')}</p>
                </div>

                <label className="rounded-lg border border-border bg-muted/30 p-4 cursor-pointer">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h3 className="font-heading text-sm font-bold text-foreground">{t('cookieAnalyticsTitle')}</h3>
                    <input
                      type="checkbox"
                      checked={analyticsEnabled}
                      onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                      className="h-4 w-4 accent-current"
                      aria-label={t('cookieAnalyticsTitle')}
                    />
                  </div>
                  <p className="text-xs leading-relaxed text-foreground/65">{t('cookieAnalyticsText')}</p>
                </label>
              </div>

              <p className="mt-3 text-xs leading-relaxed text-foreground/55">{t('cookieConsentNote')}</p>

              <div className="mt-4 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setDetailsOpen(false)}
                  className="rounded-md border border-foreground/20 px-5 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  {t('cookieBack')}
                </button>
                <button
                  type="button"
                  onClick={() => finish(analyticsEnabled)}
                  className="rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                >
                  {t('cookieSave')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
